import { useState } from 'react'
import { Id } from '../../convex/_generated/dataModel'
import { CallInitiationStep } from '../components/onboarding/CallInitiationStep'
import { CallStatusDisplay } from '../components/onboarding/CallStatusDisplay'

type Step = 'initiate' | 'active'

/**
 * Voice onboarding flow container
 * Manages phone-based onboarding where AI calls the caregiver
 */
export function VoiceOnboardingFlow() {
  const [currentStep, setCurrentStep] = useState<Step>('initiate')
  const [callId, setCallId] = useState<Id<'calls'> | null>(null)

  const handleCallInitiated = (newCallId: string) => {
    setCallId(newCallId as Id<'calls'>)
    setCurrentStep('active')
  }

  const handleRetry = () => {
    // Go back to initiation step to try again
    setCurrentStep('initiate')
    setCallId(null)
  }

  const containerStyles = {
    minHeight: '100vh',
    background: 'linear-gradient(to bottom, #f0f9ff, #ffffff)', // sky-50 to white
    fontFamily: 'system-ui, -apple-system, sans-serif',
  }

  const headerStyles = {
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
    backdropFilter: 'blur(8px)',
    borderBottom: '1px solid #f3f4f6',
    padding: '1rem 0',
    marginBottom: currentStep === 'active' ? '0.75rem' : '2rem',
  }

  const headerContentStyles = {
    maxWidth: '1280px',
    margin: '0 auto',
    padding: '0 1rem',
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
  }

  const contentWrapperStyles = {
    padding: '2rem 1rem',
  }

  const contentStyles = {
    maxWidth: currentStep === 'active' ? '1400px' : '600px',
    margin: '0 auto',
    backgroundColor: currentStep === 'active' ? 'transparent' : '#fff',
    borderRadius: currentStep === 'active' ? '0' : '16px',
    padding: currentStep === 'active' ? '0' : '2rem',
    boxShadow: currentStep === 'active' ? 'none' : '0 1px 3px rgba(0, 0, 0, 0.1)',
    transition: 'all 0.3s ease',
  }

  return (
    <div style={containerStyles}>
      {/* Header */}
      <div style={headerStyles}>
        <div style={headerContentStyles}>
          <img
            src="/favicon.png"
            alt="Little Sister Logo"
            style={{
              height: '40px',
              width: '40px',
              objectFit: 'contain',
            }}
          />
          <span
            style={{
              fontSize: '1.5rem',
              fontWeight: 700,
              color: '#111827',
            }}
          >
            Little Sister
          </span>
        </div>
      </div>

      {/* Content */}
      <div style={contentWrapperStyles}>
        <div style={contentStyles}>
          {currentStep === 'initiate' && <CallInitiationStep onNext={handleCallInitiated} />}

          {currentStep === 'active' && callId && (
            <CallStatusDisplay callId={callId} onRetry={handleRetry} />
          )}
        </div>
      </div>
    </div>
  )
}
